import { format } from "timeago.js";

function ensureUtc(time) {
  if (typeof time === 'string' && !time.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(time)) {
    return time + 'Z';
  }
  return time;
}

export default class Utils {
  /**
   * Resolves how an entity (org, blob, user) is identified in the UI: its name if it has one, its
   * alias otherwise. `fallback` covers the window before the entity has loaded, where callers pass
   * the alias from the route params.
   */
  static displayName(entity, fallback) {
    if (entity) {
      if (entity.name) return entity.name;
      if (entity.alias) return entity.alias;
    }
    return fallback;
  }

  /**
   * Resolves how a session or schedule is identified in a single-label slot: its alias if it has
   * one, its server-generated id otherwise. The alias is optional and is omitted from responses
   * entirely when unset, so absence is the ordinary case, not an error.
   *
   * For surfaces that show BOTH (grid columns, detail rails) do not use this — render the id and the
   * alias in their own slots so the id stays canonical and copyable.
   */
  static aliasOrId(object) {
    if (!object) return undefined;
    return object.alias || object.id;
  }

  /**
   * The leading fragment of an id, for surfaces where the whole UUID would crowd out the name. A
   * service account has no alias, so its id is its only handle — every site that shows a short id
   * also offers the full one, on a `title` or on the clipboard.
   */
  static shortId(id) {
    if ( !id ) return "";
    return String(id).slice(0, 8);
  }

  /**
   * The message to show for a failed API call.
   *
   * The API explains a refusal in the response body: minting an org- or blob-scoped API key while
   * acting as a service account comes back as a 400 whose `message` names what to do instead. Axios
   * puts the useless generic "Request failed with status code 400" on `.message`. So read the body
   * first, fall back to the transport message for failures that carry no response at all (network
   * errors, timeouts), and to a generic string last.
   *
   * Api#authErrorInterceptor copies the body message onto `.message` already — but only for calls
   * that reject on axiosRefresh. A request replayed after a token refresh goes out on axiosAuth,
   * which carries no response interceptor, and rejects with the raw axios error. Reading the body
   * here is what makes the rendered message right on either transport.
   */
  static errorMessage(error) {
    return error?.response?.data?.message || error?.message || "Unknown error";
  }

  static formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  static formatTimeAgo(time) {
    return format(time + " UTC");
  }

  static formatDateTime(time, timezone) {
    const options = {
      weekday: "long", year: "numeric", month: "long", day: "numeric",
      hour: "numeric", minute: "numeric", second: "numeric"
    };
    if (timezone) options.timeZone = timezone;
    return new Date(timezone ? ensureUtc(time) : time).toLocaleDateString('en-us', options);
  }

  // Format a naive datetime string (no timezone suffix) like "2025-03-12T15:30:30"
  // by treating its components as-is, without applying browser local interpretation.
  // Used for fields like invocation_time which carry their own timezone separately.
  static formatNaiveDateTime(time) {
    if (!time) return "";
    const [datePart, timePart] = time.split("T");
    if (!datePart || !timePart) return time;
    const [y, mo, d] = datePart.split("-").map(Number);
    const [h, mi, sec] = timePart.split(":").map(Number);
    const date = new Date(Date.UTC(y, mo - 1, d, h, mi, sec || 0));
    return date.toLocaleString('en-US', {
      timeZone: 'UTC',
      year: "numeric", month: "long", day: "numeric",
      hour: "numeric", minute: "numeric", second: "numeric"
    });
  }

  static formatCompactDateTime(time, timezone) {
    const options = {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit"
    };
    if (timezone) options.timeZone = timezone;
    return new Date(timezone ? ensureUtc(time) : time).toLocaleString('en-US', options);
  }

  static formatTime(time, timezone) {
    const options = {
      hour: "numeric", minute: "numeric", second: "numeric"
    };
    if (timezone) options.timeZone = timezone;
    return new Date(timezone ? ensureUtc(time) : time).toLocaleTimeString('en-us', options);
  }

  static currentTimeUtcIso() {
    return new Date().toISOString().slice(0, -1);
  }

  static deepCopy(obj) {
    return JSON.parse(JSON.stringify(obj));
  }

  static generateUniqueId() {
    return crypto.randomUUID();
  }

  /**
   * mm:ss for a remaining duration well under an hour, e.g. an acting session's time left. Floors to
   * whole seconds — the display has one-second granularity by construction — and clamps a negative
   * or non-finite remainder to "0:00" rather than ever rendering a negative value or NaN. Minutes are
   * not zero-padded (e.g. "5:03", not "05:03"); seconds always are.
   */
  static formatCountdown(remainingMs) {
    const totalSeconds = Number.isFinite(remainingMs) ? Math.max(0, Math.floor(remainingMs / 1000)) : 0;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  }
}
